import React, { useState } from 'react';
import { Button } from '../shared/Button';
import { Card } from '../shared/Card';
import ReactPlayer from 'react-player';
interface WorkoutPlanProps {
  exercises: any[];
  onBack: () => void;
  onStart: () => void;
}
export const WorkoutPlan = ({
  exercises,
  onBack,
  onStart
}: WorkoutPlanProps) => {
  const [expandedExercise, setExpandedExercise] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(false);
  // Calculate estimated workout time
  const calculateEstimatedTime = () => {
    // Assuming 1 minute per set plus rest time
    const totalSets = exercises.length > 0 ? 3 : 0; // Assuming 3 sets for each part
    const restTimePerSet = 1; // 1 minute rest
    const timePerExercise = 1; // 1 minute per exercise
    // Part A and Part B, each with their exercises
    const partAExercises = 2;
    const partBExercises = 3; // Including cardio
    const totalTime = totalSets * (partAExercises * timePerExercise + restTimePerSet + (partBExercises * timePerExercise + restTimePerSet));
    return totalTime;
  };
  // Group equipment needed
  const getEquipmentNeeded = () => {
    const equipment = [{
      name: '35lb barbell',
      count: 1
    }, {
      name: '15lb plate',
      count: 2
    }, {
      name: '25lb plate',
      count: 2
    }, {
      name: '20lb dumbbell',
      count: 2
    }, {
      name: 'Jumprope',
      count: 1
    }];
    return equipment;
  };
  // Toggle exercise details
  const toggleExerciseDetails = exerciseId => {
    if (expandedExercise === exerciseId) {
      setExpandedExercise(null);
      setPlayingVideo(false);
    } else {
      setExpandedExercise(exerciseId);
      setPlayingVideo(false);
    }
  };
  const handlePlayVideo = () => {
    setPlayingVideo(true);
  };
  const equipment = getEquipmentNeeded();
  const estimatedTime = calculateEstimatedTime();
  // Create a simplified workout structure for display
  const partAExercises = exercises.slice(0, 2);
  const partBExercises = exercises.slice(2, 4);
  const cardio = exercises.find(ex => ex.isCardio) || {
    name: 'Jump Rope',
    trackingType: 'time',
    target: 60
  };
  // Get exercise form tips based on exercise ID
  const getExerciseTips = exerciseId => {
    const tips = {
      bench: ['Keep your feet flat on the floor', 'Maintain a slight arch in your lower back', 'Lower the bar to your mid-chest', 'Keep your wrists straight throughout the movement'],
      squat: ['Keep your chest up and back straight', 'Push your knees outward as you descend', 'Descend until your thighs are parallel to the ground', 'Drive through your heels on the way up'],
      deadlift: ['Keep the bar close to your body throughout the movement', 'Maintain a neutral spine position', 'Drive through your heels and push your hips forward', 'Keep your core tight throughout the lift'],
      warmup: ['Start with a light intensity to gradually increase heart rate', 'Focus on moving all major joints through their range of motion', 'Spend at least 5 minutes warming up before intense exercise', 'Include dynamic movements that mimic your workout exercises'],
      stretch: ['Move slowly into each stretch position', 'Hold each stretch for 15-30 seconds', 'Breathe deeply and relax into the stretch', "Don't bounce or force the stretch beyond comfort"],
      cooldown: ['Gradually reduce intensity to bring heart rate down', 'Include static stretches to improve flexibility', 'Focus on the muscle groups you worked during your session', 'Take deep breaths to help your body recover'],
      default: ['Keep your back straight throughout the movement', 'Breathe out during the exertion phase', "Control the movement, don't rush", "Don't lock your joints at full extension"]
    };
    return tips[exerciseId] || tips.default;
  };
  // Check if all selections have been made
  const areAllSelectionsComplete = exercises && exercises.length >= 4;
  return <div className="flex flex-col h-full" data-id="element-298">
      <div className="p-6" data-id="element-299">
        <div className="flex justify-between items-center mb-4" data-id="element-300">
          <button onClick={onBack} className="text-gray-500" data-id="element-301">
            ← Back
          </button>
          <h1 className="text-xl font-bold text-gray-900" data-id="element-302">Workout Plan</h1>
          <button className="text-blue-500" data-id="element-303">Edit</button>
        </div>
        <div className="bg-blue-50 rounded-xl p-4 mb-6 flex items-center" data-id="element-304">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3 text-blue-500" data-id="element-305">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-306">
              <circle cx="12" cy="12" r="10" data-id="element-307"></circle>
              <polyline points="12 6 12 12 16 14" data-id="element-308"></polyline>
            </svg>
          </div>
          <div data-id="element-309">
            <p className="text-blue-800 font-medium" data-id="element-310">Estimated Time</p>
            <p className="text-blue-600" data-id="element-311">{estimatedTime} minutes</p>
          </div>
        </div>
        <div className="mb-6" data-id="element-312">
          <h2 className="font-bold text-gray-900 mb-3" data-id="element-313">Equipment Needed</h2>
          <div className="space-y-2" data-id="element-314">
            {equipment.map((item, index) => <div key={index} className="flex items-center" data-id="element-315">
                <span data-id="element-316">{item.name}</span>
                {item.count > 1 && <span className="ml-1 text-gray-500" data-id="element-317">x{item.count}</span>}
              </div>)}
          </div>
        </div>
      </div>
      <div className="flex-1 px-6 pb-6 overflow-y-auto" data-id="element-318">
        <div className="space-y-6" data-id="element-319">
          {/* Workout Structure heading only */}
          <div className="mb-6" data-id="element-320">
            <h2 className="font-bold text-gray-900 mb-3" data-id="element-321">Workout Structure</h2>
          </div>
          {/* Warm-up Section */}
          <div data-id="element-322">
            <h2 className="font-bold text-lg text-gray-900 mb-3" data-id="element-323">Warm-up</h2>
            <Card className="p-4 mb-4" data-id="element-324">
              <div className="rounded-lg overflow-hidden" data-id="element-325">
                <div className="flex items-center justify-between p-2 bg-gray-50 cursor-pointer" onClick={() => toggleExerciseDetails('warmup')} data-id="element-326">
                  <div className="flex items-center" data-id="element-327">
                    <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center mr-2 text-yellow-500 text-sm" data-id="element-328">
                      W
                    </div>
                    <span data-id="element-329">Light Cardio</span>
                  </div>
                  <div className="flex items-center text-gray-500 text-sm" data-id="element-330">
                    5 mins
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-1 transition-transform ${expandedExercise === 'warmup' ? 'rotate-90' : ''}`} data-id="element-331">
                      <path d="m9 18 6-6-6-6" data-id="element-332"></path>
                    </svg>
                  </div>
                </div>
                {expandedExercise === 'warmup' && <div className="p-3 bg-gray-100 border-t border-gray-200" data-id="element-333">
                    <div className="flex mb-3" data-id="element-334">
                      <div className="w-20 h-20 bg-gray-200 rounded-md overflow-hidden mr-3 flex-shrink-0" data-id="element-335">
                        <img src={`https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80`} alt="Warm-up" className="w-full h-full object-cover" data-id="element-336" />
                      </div>
                      <div data-id="element-337">
                        <h4 className="font-medium mb-1" data-id="element-338">Warm-up Tips:</h4>
                        <ul className="text-sm text-gray-600 space-y-1" data-id="element-339">
                          {getExerciseTips('warmup').slice(0, 2).map((tip, tipIdx) => <li key={tipIdx} className="flex items-start" data-id="element-340">
                                <span className="text-green-500 mr-1" data-id="element-341">✓</span>
                                <span data-id="element-342">{tip}</span>
                              </li>)}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm" data-id="element-343">
                      <div data-id="element-344">
                        <span className="font-medium" data-id="element-345">Duration: </span>
                        <span data-id="element-346">5 minutes</span>
                      </div>
                      <button className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs" onClick={e => {
                    e.stopPropagation();
                    handlePlayVideo();
                  }} data-id="element-347">
                        Watch Demo
                      </button>
                    </div>
                  </div>}
              </div>
            </Card>
          </div>
          {/* Dynamic Stretching Section */}
          <div data-id="element-348">
            <h2 className="font-bold text-lg text-gray-900 mb-3" data-id="element-349">
              Dynamic Stretching
            </h2>
            <Card className="p-4 mb-4" data-id="element-350">
              <div className="rounded-lg overflow-hidden" data-id="element-351">
                <div className="flex items-center justify-between p-2 bg-gray-50 cursor-pointer" onClick={() => toggleExerciseDetails('stretching')} data-id="element-352">
                  <div className="flex items-center" data-id="element-353">
                    <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2 text-purple-500 text-sm" data-id="element-354">
                      S
                    </div>
                    <span data-id="element-355">Dynamic Movement Prep</span>
                  </div>
                  <div className="flex items-center text-gray-500 text-sm" data-id="element-356">
                    3 mins
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-1 transition-transform ${expandedExercise === 'stretching' ? 'rotate-90' : ''}`} data-id="element-357">
                      <path d="m9 18 6-6-6-6" data-id="element-358"></path>
                    </svg>
                  </div>
                </div>
                {expandedExercise === 'stretching' && <div className="p-3 bg-gray-100 border-t border-gray-200" data-id="element-359">
                    <div className="flex mb-3" data-id="element-360">
                      <div className="w-20 h-20 bg-gray-200 rounded-md overflow-hidden mr-3 flex-shrink-0" data-id="element-361">
                        <img src={`https://images.unsplash.com/photo-1566241142559-40e1dab266c6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80`} alt="Dynamic Stretching" className="w-full h-full object-cover" data-id="element-362" />
                      </div>
                      <div data-id="element-363">
                        <h4 className="font-medium mb-1" data-id="element-364">Stretching Tips:</h4>
                        <ul className="text-sm text-gray-600 space-y-1" data-id="element-365">
                          {getExerciseTips('stretch').slice(0, 2).map((tip, tipIdx) => <li key={tipIdx} className="flex items-start" data-id="element-366">
                                <span className="text-green-500 mr-1" data-id="element-367">✓</span>
                                <span data-id="element-368">{tip}</span>
                              </li>)}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm" data-id="element-369">
                      <div data-id="element-370">
                        <span className="font-medium" data-id="element-371">Duration: </span>
                        <span data-id="element-372">3 minutes</span>
                      </div>
                      <button className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs" onClick={e => {
                    e.stopPropagation();
                    handlePlayVideo();
                  }} data-id="element-373">
                        Watch Demo
                      </button>
                    </div>
                  </div>}
              </div>
            </Card>
          </div>
          <div data-id="element-374">
            <h2 className="font-bold text-lg text-gray-900 mb-3" data-id="element-375">Part A</h2>
            <Card className="p-4 mb-4" data-id="element-376">
              {[1, 2, 3].map(setNum => <div key={setNum} className="mb-3 last:mb-0" data-id="element-377">
                  <h3 className="font-medium text-gray-700 mb-2" data-id="element-378">
                    Set {setNum}
                  </h3>
                  <div className="space-y-2" data-id="element-379">
                    {partAExercises.map((exercise, idx) => <div key={idx} className="rounded-lg overflow-hidden" data-id="element-380">
                        <div className="flex items-center justify-between p-2 bg-gray-50 cursor-pointer" onClick={() => toggleExerciseDetails(exercise.id + setNum + 'A')} data-id="element-381">
                          <div className="flex items-center" data-id="element-382">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-blue-500 text-sm" data-id="element-383">
                              {idx + 1}
                            </div>
                            <span data-id="element-384">{exercise.name}</span>
                          </div>
                          <div className="flex items-center text-gray-500 text-sm" data-id="element-385">
                            {exercise.reps || 10} reps
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-1 transition-transform ${expandedExercise === exercise.id + setNum + 'A' ? 'rotate-90' : ''}`} data-id="element-386">
                              <path d="m9 18 6-6-6-6" data-id="element-387"></path>
                            </svg>
                          </div>
                        </div>
                        {expandedExercise === exercise.id + setNum + 'A' && <div className="p-3 bg-gray-100 border-t border-gray-200" data-id="element-388">
                            <div className="flex mb-3" data-id="element-389">
                              <div className="w-20 h-20 bg-gray-200 rounded-md overflow-hidden mr-3 flex-shrink-0" data-id="element-390">
                                <img src={`https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80`} alt={exercise.name} className="w-full h-full object-cover" data-id="element-391" />
                              </div>
                              <div data-id="element-392">
                                <h4 className="font-medium mb-1" data-id="element-393">Form Tips:</h4>
                                <ul className="text-sm text-gray-600 space-y-1" data-id="element-394">
                                  {getExerciseTips(exercise.id).slice(0, 2).map((tip, tipIdx) => <li key={tipIdx} className="flex items-start" data-id="element-395">
                                        <span className="text-green-500 mr-1" data-id="element-396">
                                          ✓
                                        </span>
                                        <span data-id="element-397">{tip}</span>
                                      </li>)}
                                </ul>
                              </div>
                            </div>
                            <div className="flex justify-between items-center text-sm" data-id="element-398">
                              <div data-id="element-399">
                                <span className="font-medium" data-id="element-400">Target: </span>
                                <span data-id="element-401">{exercise.reps || 10} reps</span>
                                {exercise.weight && <span data-id="element-402"> at {exercise.weight}lb</span>}
                              </div>
                              <button className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs" onClick={e => {
                        e.stopPropagation();
                        handlePlayVideo();
                      }} data-id="element-403">
                                Watch Demo
                              </button>
                            </div>
                          </div>}
                      </div>)}
                    <div className="p-2 bg-gray-50 rounded-lg text-gray-500 text-sm flex items-center" data-id="element-404">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-405">
                        <circle cx="12" cy="12" r="10" data-id="element-406"></circle>
                        <polyline points="12 6 12 12 16 14" data-id="element-407"></polyline>
                      </svg>
                      Rest 1 minute
                    </div>
                  </div>
                </div>)}
            </Card>
          </div>
          <div data-id="element-408">
            <h2 className="font-bold text-lg text-gray-900 mb-3" data-id="element-409">Part B</h2>
            <Card className="p-4" data-id="element-410">
              {[1, 2, 3].map(setNum => <div key={setNum} className="mb-3 last:mb-0" data-id="element-411">
                  <h3 className="font-medium text-gray-700 mb-2" data-id="element-412">
                    Set {setNum}
                  </h3>
                  <div className="space-y-2" data-id="element-413">
                    {partBExercises.map((exercise, idx) => <div key={idx} className="rounded-lg overflow-hidden" data-id="element-414">
                        <div className="flex items-center justify-between p-2 bg-gray-50 cursor-pointer" onClick={() => toggleExerciseDetails(exercise.id + setNum + 'B')} data-id="element-415">
                          <div className="flex items-center" data-id="element-416">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-blue-500 text-sm" data-id="element-417">
                              {idx + 1}
                            </div>
                            <span data-id="element-418">{exercise.name}</span>
                          </div>
                          <div className="flex items-center text-gray-500 text-sm" data-id="element-419">
                            {exercise.reps || 10} reps
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-1 transition-transform ${expandedExercise === exercise.id + setNum + 'B' ? 'rotate-90' : ''}`} data-id="element-420">
                              <path d="m9 18 6-6-6-6" data-id="element-421"></path>
                            </svg>
                          </div>
                        </div>
                        {expandedExercise === exercise.id + setNum + 'B' && <div className="p-3 bg-gray-100 border-t border-gray-200" data-id="element-422">
                            <div className="flex mb-3" data-id="element-423">
                              <div className="w-20 h-20 bg-gray-200 rounded-md overflow-hidden mr-3 flex-shrink-0" data-id="element-424">
                                <img src={`https://images.unsplash.com/photo-1534438327276-14e5300c3a48?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80`} alt={exercise.name} className="w-full h-full object-cover" data-id="element-425" />
                              </div>
                              <div data-id="element-426">
                                <h4 className="font-medium mb-1" data-id="element-427">Form Tips:</h4>
                                <ul className="text-sm text-gray-600 space-y-1" data-id="element-428">
                                  {getExerciseTips(exercise.id).slice(0, 2).map((tip, tipIdx) => <li key={tipIdx} className="flex items-start" data-id="element-429">
                                        <span className="text-green-500 mr-1" data-id="element-430">
                                          ✓
                                        </span>
                                        <span data-id="element-431">{tip}</span>
                                      </li>)}
                                </ul>
                              </div>
                            </div>
                            <div className="flex justify-between items-center text-sm" data-id="element-432">
                              <div data-id="element-433">
                                <span className="font-medium" data-id="element-434">Target: </span>
                                <span data-id="element-435">{exercise.reps || 10} reps</span>
                                {exercise.weight && <span data-id="element-436"> at {exercise.weight}lb</span>}
                              </div>
                              <button className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs" onClick={e => {
                        e.stopPropagation();
                        handlePlayVideo();
                      }} data-id="element-437">
                                Watch Demo
                              </button>
                            </div>
                          </div>}
                      </div>)}
                    <div className="rounded-lg overflow-hidden" data-id="element-438">
                      <div className="flex items-center justify-between p-2 bg-gray-50 cursor-pointer" onClick={() => toggleExerciseDetails('cardio' + setNum)} data-id="element-439">
                        <div className="flex items-center" data-id="element-440">
                          <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-2 text-green-500 text-sm" data-id="element-441">
                            C
                          </div>
                          <span data-id="element-442">{cardio.name}</span>
                        </div>
                        <div className="flex items-center text-gray-500 text-sm" data-id="element-443">
                          {cardio.target}{' '}
                          {cardio.trackingType === 'calories' ? 'cal' : 'sec'}
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-1 transition-transform ${expandedExercise === 'cardio' + setNum ? 'rotate-90' : ''}`} data-id="element-444">
                            <path d="m9 18 6-6-6-6" data-id="element-445"></path>
                          </svg>
                        </div>
                      </div>
                      {expandedExercise === 'cardio' + setNum && <div className="p-3 bg-gray-100 border-t border-gray-200" data-id="element-446">
                          <div className="flex mb-3" data-id="element-447">
                            <div className="w-20 h-20 bg-gray-200 rounded-md overflow-hidden mr-3 flex-shrink-0" data-id="element-448">
                              <img src={`https://images.unsplash.com/photo-1434596922112-19c563067271?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80`} alt={cardio.name} className="w-full h-full object-cover" data-id="element-449" />
                            </div>
                            <div data-id="element-450">
                              <h4 className="font-medium mb-1" data-id="element-451">Cardio Tips:</h4>
                              <ul className="text-sm text-gray-600 space-y-1" data-id="element-452">
                                <li className="flex items-start" data-id="element-453">
                                  <span className="text-green-500 mr-1" data-id="element-454">✓</span>
                                  <span data-id="element-455">Start at a comfortable pace</span>
                                </li>
                                <li className="flex items-start" data-id="element-456">
                                  <span className="text-green-500 mr-1" data-id="element-457">✓</span>
                                  <span data-id="element-458">Focus on steady breathing</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                          <div className="flex justify-between items-center text-sm" data-id="element-459">
                            <div data-id="element-460">
                              <span className="font-medium" data-id="element-461">Target: </span>
                              <span data-id="element-462">
                                {cardio.target}{' '}
                                {cardio.trackingType === 'calories' ? 'calories' : 'seconds'}
                              </span>
                            </div>
                            <button className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs" onClick={e => {
                        e.stopPropagation();
                        handlePlayVideo();
                      }} data-id="element-463">
                              Watch Demo
                            </button>
                          </div>
                        </div>}
                    </div>
                    <div className="p-2 bg-gray-50 rounded-lg text-gray-500 text-sm flex items-center" data-id="element-464">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-465">
                        <circle cx="12" cy="12" r="10" data-id="element-466"></circle>
                        <polyline points="12 6 12 12 16 14" data-id="element-467"></polyline>
                      </svg>
                      Rest 1 minute
                    </div>
                  </div>
                </div>)}
            </Card>
          </div>
          {/* Start Workout button moved here, below Part B */}
          <div className="mt-6 mb-6" data-id="element-468">
            <Button onClick={onStart} variant="primary" fullWidth className="py-4 text-lg font-bold" disabled={!areAllSelectionsComplete} data-id="element-469">
              {areAllSelectionsComplete ? 'Start Workout' : 'Complete Exercise Selection to Start'}
            </Button>
          </div>
          {/* Cool Down Section */}
          <div data-id="element-470">
            <h2 className="font-bold text-lg text-gray-900 mb-3" data-id="element-471">Cool Down</h2>
            <Card className="p-4 mb-4" data-id="element-472">
              <div className="rounded-lg overflow-hidden" data-id="element-473">
                <div className="flex items-center justify-between p-2 bg-gray-50 cursor-pointer" onClick={() => toggleExerciseDetails('cooldown')} data-id="element-474">
                  <div className="flex items-center" data-id="element-475">
                    <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2 text-blue-500 text-sm" data-id="element-476">
                      C
                    </div>
                    <span data-id="element-477">Static Stretching</span>
                  </div>
                  <div className="flex items-center text-gray-500 text-sm" data-id="element-478">
                    5 mins
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`ml-1 transition-transform ${expandedExercise === 'cooldown' ? 'rotate-90' : ''}`} data-id="element-479">
                      <path d="m9 18 6-6-6-6" data-id="element-480"></path>
                    </svg>
                  </div>
                </div>
                {expandedExercise === 'cooldown' && <div className="p-3 bg-gray-100 border-t border-gray-200" data-id="element-481">
                    <div className="flex mb-3" data-id="element-482">
                      <div className="w-20 h-20 bg-gray-200 rounded-md overflow-hidden mr-3 flex-shrink-0" data-id="element-483">
                        <img src={`https://images.unsplash.com/photo-1518611012118-696072aa579a?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=300&q=80`} alt="Cool Down" className="w-full h-full object-cover" data-id="element-484" />
                      </div>
                      <div data-id="element-485">
                        <h4 className="font-medium mb-1" data-id="element-486">Cool Down Tips:</h4>
                        <ul className="text-sm text-gray-600 space-y-1" data-id="element-487">
                          {getExerciseTips('cooldown').slice(0, 2).map((tip, tipIdx) => <li key={tipIdx} className="flex items-start" data-id="element-488">
                                <span className="text-green-500 mr-1" data-id="element-489">✓</span>
                                <span data-id="element-490">{tip}</span>
                              </li>)}
                        </ul>
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-sm" data-id="element-491">
                      <div data-id="element-492">
                        <span className="font-medium" data-id="element-493">Duration: </span>
                        <span data-id="element-494">5 minutes</span>
                      </div>
                      <button className="px-3 py-1 bg-blue-500 text-white rounded-full text-xs" onClick={e => {
                    e.stopPropagation();
                    handlePlayVideo();
                  }} data-id="element-495">
                        Watch Demo
                      </button>
                    </div>
                  </div>}
              </div>
            </Card>
          </div>
        </div>
      </div>
      {expandedExercise && playingVideo && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-10 p-4" data-id="element-496">
          <div className="bg-white rounded-xl w-full max-w-md overflow-hidden" data-id="element-497">
            <div className="relative pt-[56.25%]" data-id="element-498">
              <ReactPlayer url="https://www.youtube.com/watch?v=IODxDxX7oi4" playing={true} controls={true} width="100%" height="100%" style={{
            position: 'absolute',
            top: 0,
            left: 0
          }} onEnded={() => setPlayingVideo(false)} data-id="element-499" />
            </div>
            <div className="p-4" data-id="element-500">
              <h3 className="font-bold text-lg mb-2" data-id="element-501">Proper Form Tips</h3>
              <ul className="space-y-2 mb-4" data-id="element-502">
                <li className="flex items-start" data-id="element-503">
                  <span className="text-green-500 mr-2" data-id="element-504">✓</span>
                  <span data-id="element-505">Keep your back straight throughout the movement</span>
                </li>
                <li className="flex items-start" data-id="element-506">
                  <span className="text-green-500 mr-2" data-id="element-507">✓</span>
                  <span data-id="element-508">Breathe out during the exertion phase</span>
                </li>
                <li className="flex items-start" data-id="element-509">
                  <span className="text-green-500 mr-2" data-id="element-510">✓</span>
                  <span data-id="element-511">Control the movement, don't rush</span>
                </li>
                <li className="flex items-start" data-id="element-512">
                  <span className="text-red-500 mr-2" data-id="element-513">✗</span>
                  <span data-id="element-514">Don't lock your joints at full extension</span>
                </li>
              </ul>
              <button onClick={() => {
            setPlayingVideo(false);
          }} className="w-full py-3 bg-blue-500 text-white rounded-lg" data-id="element-515">
                Close
              </button>
            </div>
          </div>
        </div>}
      {/* Fixed action buttons at the bottom - Only Customize Workout button remains */}
      <div className="sticky bottom-0 left-0 right-0 p-4 border-t border-gray-100 bg-white" data-id="element-516">
        <div className="space-y-3 max-w-md mx-auto" data-id="element-517">
          <Button variant="outline" fullWidth className="py-3" onClick={() => onBack()} data-id="element-518">
            <div className="flex items-center justify-center" data-id="element-519">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2" data-id="element-520">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" data-id="element-521"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" data-id="element-522"></path>
              </svg>
              Customize Workout
            </div>
          </Button>
        </div>
      </div>
    </div>;
};